'use client';

import {
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import Link from 'next/link';
import React, { useMemo, useState } from 'react';

import { Section } from '~components/Section/Section';
import { SectionSubscribeAside } from '~components/SectionSubscribe/SectionSunscribeAside/SectionSubscribeAside';
import { mapContentToMenu } from '~containers/ArticlePage/ArticlePageContent/ArticlePageContent.utils';
import { ArticlePageContentBlocks } from '~containers/ArticlePage/ArticlePageContentBlocks/ArticlePageContentBlocks';
import { useHeaderVisibility } from '~hooks/useHeaderVisibility';
import { useIntersectionObserver } from '~hooks/useIntersectionObserver';
import Share from '~public/static/images/icons/share.svg';

import styles from './ArticlePageContent.module.scss';
import { ArticlePageContentProps } from './ArticlePageContent.types';

export function ArticlePageContent({
  content = [],
  sectionSubscribe,
  tags,
  subscribeId,
}: ArticlePageContentProps) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { headerVisible } = useHeaderVisibility();
  const contentBlocks = sectionSubscribe.isHidden
    ? content
    : subscribeId
      ? [
          ...content.slice(0, subscribeId - 1),
          sectionSubscribe,
          ...content.slice(subscribeId - 1),
        ]
      : [...content, sectionSubscribe];
  const { activeSection, registerSection, unregisterSection } =
    useIntersectionObserver();

  const menu = useMemo(
    () => (content?.length ? mapContentToMenu(content) : undefined),
    [content],
  );

  const handleShareButtonClick = () => {
    copy(window.location.href);
    setTooltipOpen(true);
    setTimeout(() => {
      setTooltipOpen(false);
    }, 2000);
  };

  return (
    <Section className={styles.container}>
      <div className={styles.content}>
        {contentBlocks?.length && (
          <ArticlePageContentBlocks
            content={contentBlocks}
            registerSection={registerSection}
            unregisterSection={unregisterSection}
          />
        )}

        <footer
          className={classNames(styles.footer, {
            [styles.noTags]: !tags?.length,
          })}
        >
          {!!tags?.length && (
            <div className={styles.tags}>
              Теги:{' '}
              {tags.map((tag, idx) => (
                <span key={tag.id} className={styles.tagsItem}>
                  {tag.text}
                  {idx !== tags.length - 1 && ', '}
                </span>
              ))}
            </div>
          )}
          <div className={styles.btnContainer}>
            <div
              className={classNames(styles.tooltip, {
                [styles.isOpen]: tooltipOpen,
              })}
            >
              <Typography
                tagName={TypographySizes.span}
                mods={[TypographyMods.captionS]}
              >
                Ссылка скопирована
              </Typography>
            </div>
            <div
              className={styles.shareButton}
              onClick={handleShareButtonClick}
            >
              <Share className={styles.icon} />
              <Typography
                mods={[TypographyMods.bodySAlt]}
                tagName={TypographySizes.span}
              >
                Поделиться
              </Typography>
            </div>
          </div>
        </footer>
      </div>
      <aside
        className={classNames(styles.aside, headerVisible && styles.headerUp)}
      >
        {!!menu?.length && (
          <div className={styles.menu}>
            Содержание
            <nav>
              <ul className={styles.menuList}>
                {menu.map((menuItem, key) => (
                  <li
                    key={menuItem.hash + key}
                    className={classNames(
                      styles.menuItem,
                      menuItem.hash === activeSection && styles.active,
                    )}
                  >
                    <Link
                      className={styles.menuLink}
                      href={`#${menuItem.hash}`}
                    >
                      {menuItem.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
        {!sectionSubscribe.isHidden && (
          <SectionSubscribeAside {...sectionSubscribe} />
        )}
      </aside>
    </Section>
  );
}
